/* DP4Y Cart Rules
   - Free stones: enforce exactly 1 when cart has other (non-addon) items; remove when cart empty of other items
   - Photo check: allow max 1; also keep the "Foto check actief" state in sync

   This script is intentionally defensive:
   - If Liquid could not resolve variant ids (e.g. product not published), we fallback to handles and cart line keys.
   - We avoid calling Shopify endpoints with invalid ids (prevents the red error banner).
*/
(function () {
  'use strict';

  const RULES = (window.DP4Y_CART_RULES || {});

  const PHOTO_HANDLE = RULES.photoCheckHandle || 'foto-check-service';
  const STONES_HANDLE = RULES.freeStonesHandle || 'extra-steentjes-gratis';
  const PHOTO_ID_LIQUID = Number(RULES.photoCheckVariantId || 0);
  const STONES_ID_LIQUID = Number(RULES.freeStonesVariantId || 0);

  let enforcing = false;
  let cachedProductVariantIds = Object.create(null); // handle -> variant_id

  async function fetchJSON(url, options) {
    const res = await fetch(url, Object.assign({ credentials: 'same-origin' }, options || {}));
    const txt = await res.text();
    let data;
    try { data = JSON.parse(txt); } catch (e) { data = null; }
    if (!res.ok) {
      const msg = (data && (data.description || data.message)) || res.statusText || 'Request failed';
      throw new Error(msg);
    }
    return data;
  }

  async function fetchCart() {
    return fetchJSON('/cart.js', { method: 'GET' });
  }

  function getLineItemsByHandle(cart, handle) {
    const h = String(handle || '').trim();
    if (!h) return [];
    const items = cart.items || [];
    // Shopify /cart.js does not always include a "line" field; compute it from array index (1-based).
    return items
      .map((it, idx) => Object.assign({ line: idx + 1 }, it))
      .filter(it => String(it.handle || '') === h);
  }

  function sumQty(items) {
    return (items || []).reduce((s, it) => s + Number(it.quantity || 0), 0);
  }

  function getOtherItems(cart) {
    return (cart.items || []).filter(it => {
      const h = String(it.handle || '');
      return h !== PHOTO_HANDLE && h !== STONES_HANDLE;
    });
  }

  async function changeByLine(line, quantity) {
    // Shopify: /cart/change.js expects line (1-based) OR id.
    // NOTE: line numbers can shift when the cart is updated asynchronously, so prefer changeById.
    if (!line || line < 1) return;
    await fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    });
  }

  async function changeById(variantId, quantity) {
    // Robust against line-number shifts.
    if (!variantId) return;
    await fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) })
    });
  }

  async function updateAttributes(attrs) {
    if (!attrs || typeof attrs !== 'object') return;
    await fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  async function resolveVariantIdFromHandle(handle) {
    const h = String(handle || '').trim();
    if (!h) return 0;
    if (cachedProductVariantIds[h]) return cachedProductVariantIds[h];
    try {
      const p = await fetchJSON(`/products/${encodeURIComponent(h)}.js`, { method: 'GET' });
      const v = (p && p.variants && p.variants[0] && p.variants[0].id) ? Number(p.variants[0].id) : 0;
      if (v) cachedProductVariantIds[h] = v;
      return v;
    } catch (e) {
      return 0;
    }
  }

  async function addVariant(variantId, qty) {
    const id = Number(variantId || 0);
    const q = Number(qty || 0);
    if (!id || q <= 0) return;
    await fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: q })
    });
  }

  async function enforceFreeStones(cart) {
    // We show “Altijd genoeg steentjes” as an included guarantee in the cart UI,
    // but we do NOT want a separate line-item in the cart for it.
    // So if the product is present (manually added or otherwise), remove it.
    const stonesLines = getLineItemsByHandle(cart, STONES_HANDLE);
    for (let i = 0; i < stonesLines.length; i++) {
      if (Number(stonesLines[i].quantity) > 0) {
        await changeById(stonesLines[i].variant_id, 0);
        return true;
      }
    }
    return false;
  }

  async function enforcePhotoCheck(cart) {
    const photoLines = getLineItemsByHandle(cart, PHOTO_HANDLE);
    const photoQty = sumQty(photoLines);

    // Enforce max 1 (do not auto-add)
    if (photoLines.length > 0) {
      // Keep first line at qty 1, remove rest
      const first = photoLines[0];
      if (Number(first.quantity) > 1) {
        await changeById(first.variant_id, 1);
        return { changed: true, hasPhoto: true };
      }
      for (let i = 1; i < photoLines.length; i++) {
        if (Number(photoLines[i].quantity) > 0) {
          await changeById(photoLines[i].variant_id, 0);
          return { changed: true, hasPhoto: true };
        }
      }
    }

    return { changed: false, hasPhoto: photoQty > 0 };
  }

  async function syncPhotoActiveFlag(hasPhoto) {
    // Keep badge state consistent with cart contents.
    // Some theme templates reference cart.attributes['Foto check'] (with a space)
    // while older scripts used cart.attributes.photo_check. We write both.
    // Don't store a numeric flag in the human-facing attribute; the theme
    // prints this attribute as copy in the cart UI.
    const message = hasPhoto ? 'Voorbeeld binnen 3 uur via WhatsApp / e-mail.' : '';
    const flag = hasPhoto ? '1' : '';
    try {
      await updateAttributes({
        // Human-facing copy
        'Foto check': message,
        // Machine-readable flag (kept for backwards compatibility)
        photo_check: flag,
        // Extra explicit flag (new)
        photo_check_active: flag
      });
    } catch (e) {
      // ignore
    }
  }

  async function enforceCartRules() {
    if (enforcing) return;
    enforcing = true;
    try {
      const cart = await fetchCart();

      // 1) Photo check max 1 + sync active state
      const photoRes = await enforcePhotoCheck(cart);
      if (photoRes.changed) return;

      // Keep attribute in sync even if no cart mutations were needed
      await syncPhotoActiveFlag(photoRes.hasPhoto);

      // 2) Free stones rule
      const stonesChanged = await enforceFreeStones(cart);
      if (stonesChanged) return;
    } catch (e) {
      // silent: we don't want to break the cart page
    } finally {
      enforcing = false;
    }
  }

  function attach() {
    // Run once on load
    enforceCartRules();

    // Hook into Booster events if available
    const bt = window.boosterTheme;
    if (bt && bt.events && typeof bt.events.on === 'function') {
      bt.events.on('booster:cart:add', enforceCartRules);
      bt.events.on('booster:cart:update', enforceCartRules);
      bt.events.on('booster:cart:refresh', enforceCartRules);
      bt.events.on('booster:cart:open', enforceCartRules);
    }

    // Fallback triggers: after common cart actions
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!t) return;
      if (
        t.closest('[name="add"]') ||
        t.closest('button[name="plus"]') ||
        t.closest('button[name="minus"]') ||
        t.closest('[data-cart-update]') ||
        t.closest('a[href*="/cart"]') ||
        t.closest('button')
      ) {
        setTimeout(enforceCartRules, 450);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
